import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createRefetchContainer } from 'react-relay';
import { getQueryParam } from '../../../utils/uriUtils';
import SharedModifyPortfolioItems from '../../../mutations/SharedModifyPortfolioItems';

export class FavoritesProvider extends React.Component {
    constructor(props) {
        super(props);

        this.hasAutoFavoriteItemParam = false;
        this.hasInitialFetch = false;
    }

    componentDidMount() {
        const { shouldAutoFavorite, userId } = this.props;
        this.hasAutoFavoriteItemParam = getQueryParam(window.location.href, 'follow') === 'item';
        if (shouldAutoFavorite && userId) {
            this.handleAutoFavorite();
        } else if (userId) {
            this.refetchFavorites();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.shouldFetchFavorites(prevProps)) {
            this.refetchFavorites();
            if (prevProps.shouldAutoFavorite) {
                this.handleAutoFavorite();
            }
            this.hasInitialFetch = true;
        }
    }

    handleAutoFavorite() {
        const { itemIds, relay, userId } = this.props;
        if (this.hasAutoFavoriteItemParam) {
            SharedModifyPortfolioItems.commit(relay.environment, {
                action: 'ADD',
                itemId: itemIds[0],
                portfolioType: 'HEART',
                userId,
                onCompleted: () => this.refetchFavorites(),
            });
        }
    }

    shouldFetchFavorites(prevProps) {
        const { itemIds, userId, fetchFavorites } = this.props;

        // this component should fetch data:
        // 1. on initial mount if user is logged in
        const shouldDoInitialFetch = userId && fetchFavorites && !this.hasInitialFetch;
        // 2. if user logs in later
        const userHasLoggedIn = !prevProps.userId && userId && this.initialFetch;
        // 3. if items change (applies only to search and browse)
        const receivedNewItems = prevProps.itemIds.toString() !== itemIds.toString();

        return shouldDoInitialFetch || userHasLoggedIn || receivedNewItems;
    }

    refetchFavorites() {
        const { itemIds, additionalRenderVariables, userId } = this.props;
        const userIds = userId ? [userId] : null;
        this.props.relay.refetch(
            {
                userIds,
                selectedItemIds: itemIds,
                loadPortfolioData: !!userId,
                shouldFillInFolder: !!userId,
            },
            {
                userIds,
                selectedItemIds: itemIds,
                loadPortfolioData: !!userId,
                shouldFillInFolder: !!userId,
                ...additionalRenderVariables,
            }
        );
    }

    render() {
        const {
            disable,
            location,
            userId,
            itemIds,
            environment,
            fetchHeart,
            fetchFolder,
            viewer,
        } = this.props;
        const userIds = userId ? [userId] : [];

        const favorites = {
            userId,
            itemIds,
            userIds,
            disable,
            location,
            environment,
            fetchHeart,
            fetchFolder,
            singlePortfolioItem: true,
            loadPortfolioItemData: true,
            selectedItemIds: itemIds,
            loadPortfolioData: !!userId,
            shouldFillInFolder: !!userId,
            viewer,
        };

        return this.props.children({ favorites });
    }
}

FavoritesProvider.defaultProps = {
    fetchHeart: true,
    fetchFolder: true,
    shouldAutoFavorite: false,
};

FavoritesProvider.propTypes = {
    userId: PropTypes.string,
    disable: PropTypes.bool,
    location: PropTypes.string,
    itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    environment: PropTypes.object.isRequired,
    fetchHeart: PropTypes.bool.isRequired,
    fetchFolder: PropTypes.bool.isRequired,
    children: PropTypes.func,
    additionalRenderVariables: PropTypes.object,
    relay: PropTypes.object,
    shouldAutoFavorite: PropTypes.bool,
    viewer: PropTypes.object,
    fetchFavorites: PropTypes.bool,
};

export default createRefetchContainer(
    FavoritesProvider,
    {
        viewer: graphql`
            fragment FavoritesProvider_viewer on Viewer
                @argumentDefinitions(
                    selectedItemIds: { type: "[String]", defaultValue: [] }
                    loadPortfolioData: { type: "Boolean", defaultValue: false }
                    shouldFillInFolder: { type: "Boolean", defaultValue: false }
                    userIds: { type: "[String]", defaultValue: [] }
                ) {
                ...FolderWrapper_viewer
                    @arguments(
                        selectedItemIds: $selectedItemIds
                        shouldFillInFolder: $shouldFillInFolder
                        userIds: $userIds
                    )
                ...HeartWrapper_viewer
                    @arguments(
                        loadPortfolioData: $loadPortfolioData
                        selectedItemIds: $selectedItemIds
                        userIds: $userIds
                    )
            }
        `,
    },
    graphql`
        query FavoritesProviderRefetchQuery(
            $loadPortfolioData: Boolean = false
            $selectedItemIds: [String] = []
            $userIds: [String] = []
            $shouldFillInFolder: Boolean = false
        ) {
            viewer {
                ...FavoritesProvider_viewer
                    @arguments(
                        loadPortfolioData: $loadPortfolioData
                        selectedItemIds: $selectedItemIds
                        userIds: $userIds
                        shouldFillInFolder: $shouldFillInFolder
                    )
            }
        }
    `
);
